import { useState, useEffect, useCallback } from 'react';
import { 
  isPushNotificationSupported, 
  getNotificationPermission, 
  isNotificationEnabled as checkPushEnabled,
  isDesktopNativeNotificationSupported,
  enablePushNotifications,
  disablePushNotifications,
  fetchVapidPublicKey,
  sendTestPushNotification,
  sendLocalDemoNotification
} from '@/app/utils/push-notifications';
import { toast } from 'sonner';

export type AppName =  'aql-vision';

export function useNotifications(appName: AppName, apiUrl: string, authToken: string, userEmail?: string | null) {
  const [isSupported, setIsSupported] = useState(false);
  const [permission, setPermission] = useState<NotificationPermission>('default');
  const [isEnabled, setIsEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLocalMode, setIsLocalMode] = useState(false);
  const [vapidPublicKey, setVapidPublicKey] = useState<string | null>(null);

  const refreshStatus = useCallback(() => {
    const supported = isPushNotificationSupported();
    setIsSupported(supported);
    if (supported) {
      setPermission(getNotificationPermission());
      setIsEnabled(checkPushEnabled(appName));
      
      // Check if we are in local mode (enabled but no active service worker)
      if (checkPushEnabled(appName) && 'serviceWorker' in navigator) {
        navigator.serviceWorker.getRegistration().then(reg => {
          setIsLocalMode(!reg);
        });
      }
    }
  }, [appName]);

  useEffect(() => {
    refreshStatus();
    
    // Load VAPID key with a small delay to ensure network is stable
    let isMounted = true;
    if (apiUrl) {
      const timer = setTimeout(() => {
        fetchVapidPublicKey(apiUrl).then(key => {
          if (isMounted) setVapidPublicKey(key);
        });
      }, 1000);
      return () => {
        isMounted = false;
        clearTimeout(timer);
      };
    }
  }, [appName, apiUrl, refreshStatus]);

  useEffect(() => {
    if (!isSupported || !isEnabled || !isLocalMode || !vapidPublicKey || permission !== 'granted') return;

    let isMounted = true;
    enablePushNotifications(appName, apiUrl, authToken, vapidPublicKey, {
      userEmail,
      forceResubscribe: true
    }).then((result) => {
      if (!isMounted || !result.success) return;
      setIsLocalMode(Boolean(result.error?.includes('modo local')));
    });

    return () => {
      isMounted = false;
    };
  }, [apiUrl, appName, authToken, isEnabled, isLocalMode, isSupported, permission, userEmail, vapidPublicKey]);

  const toggleNotifications = async () => {
    if (!isSupported) {
      toast.error('Notificações não são suportadas neste navegador');
      return;
    }

    if (permission === 'denied') {
      toast.error('Notificações bloqueadas no navegador. Por favor, ative nas configurações do site.');
      return;
    }

    const isDesktopNative = isDesktopNativeNotificationSupported();

    if (!isDesktopNative && !vapidPublicKey) {
      toast.error('Configuração do servidor incompleta (VAPID key em falta)');
      return;
    }

    setIsLoading(true);
    try {
      if (isEnabled) {
        const result = await disablePushNotifications(appName, apiUrl, authToken);
        if (result.success) {
          setIsEnabled(false);
          setIsLocalMode(false);
          toast.success('Notificações desativadas');
        } else {
          toast.error(result.error || 'Erro ao desativar notificações');
        }
      } else {
        const result = await enablePushNotifications(appName, apiUrl, authToken, vapidPublicKey, { userEmail });
        if (result.success) {
          setIsEnabled(true);
          setPermission('granted');
          
          // Check for local mode warning
          if (result.error && result.error.includes('modo desktop')) {
            setIsLocalMode(false);
            toast.success('Notificações nativas ativadas neste Mac.');
          } else if (result.error && result.error.includes('modo local')) {
            setIsLocalMode(true);
            toast.warning('Notificações ativadas em Modo de Compatibilidade.');
          } else {
            setIsLocalMode(false);
            toast.success('Notificações ativadas com sucesso!');
          }
        } else {
          toast.error(result.error || 'Erro ao ativar notificações');
        }
      }
    } catch (error) {
      console.error('Error toggling notifications:', error);
      toast.error('Ocorreu um erro inesperado');
    } finally {
      setIsLoading(false);
    }
  };

  const testNotification = async () => {
    if (!isEnabled) return;
    
    // If in local mode, use the local fallback
    if (isLocalMode) {
      await sendLocalDemoNotification(appName);
      return;
    }

    try {
      const success = await sendTestPushNotification(appName, apiUrl, authToken, { userEmail });
      if (success) {
        toast.info('Notificação de teste enviada!');
      } else {
        if (vapidPublicKey) {
          const repaired = await enablePushNotifications(appName, apiUrl, authToken, vapidPublicKey, {
            userEmail,
            forceResubscribe: true
          });
          if (repaired.success) {
            const retrySuccess = await sendTestPushNotification(appName, apiUrl, authToken, { userEmail });
            if (retrySuccess) {
              setIsEnabled(true);
              setIsLocalMode(false);
              toast.success('Subscrição push atualizada e teste enviado!');
              return;
            }
          }
        }

        toast.error('Falha ao enviar notificação de teste. Tentando modo local...');
        await sendLocalDemoNotification(appName);
      }
    } catch (error) {
      toast.error('Erro ao testar notificações');
    }
  };

  return {
    isSupported,
    permission,
    isEnabled,
    isLoading,
    isLocalMode,
    toggleNotifications,
    testNotification,
    refreshStatus
  };
}
